"use client";

import { useRef, Suspense, useEffect, useState, useCallback } from "react";
import { useSearchParams, usePathname } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { LoggedInHome } from "./logged-in-home";
import { LoggedOutHome } from "./logged-out-home";

function PageContent() {
  const { user, loading } = useAuth();
  const searchParams = useSearchParams();
  const [mounted, setMounted] = useState(false);
  const hasLaunchQuery = Boolean(searchParams.get("q") || searchParams.get("prefill"));
  const readyToRenderInteractiveHome = mounted && (!hasLaunchQuery || !loading);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Hide the server-rendered SEO content once the interactive version is ready
  useEffect(() => {
    if (!readyToRenderInteractiveHome) return;
    const seoEl = document.getElementById("seo-landing-content");
    if (seoEl) seoEl.style.display = "none";
  }, [readyToRenderInteractiveHome]);

  if (!readyToRenderInteractiveHome) return null;

  return user ? <LoggedInHome /> : <LoggedOutHome />;
}

function PageContentWithReset() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const resetParam = searchParams.get("_");
  const [resetKey, setResetKey] = useState(resetParam || "initial");
  const previousPathnameRef = useRef(pathname);
  const resetCounterRef = useRef(0);

  const bumpResetKey = useCallback(() => {
    resetCounterRef.current += 1;
    setResetKey(`reset-${resetCounterRef.current}`);
  }, []);

  // Only update the reset key when we're actually on the home page.
  // After message submit, replaceState changes URL to /chat/<uuid> which
  // removes the `_` param — without this guard that would flip the key
  // and remount PageContent mid-stream, destroying the active Chat.
  useEffect(() => {
    if (pathname === "/") {
      if (resetParam) {
        setResetKey((current) => (current === resetParam ? current : resetParam));
      } else if (previousPathnameRef.current !== "/") {
        bumpResetKey();
      }
    }

    previousPathnameRef.current = pathname;
  }, [pathname, resetParam, bumpResetKey]);

  useEffect(() => {
    const handleNewHomeChat = () => {
      bumpResetKey();
    };

    window.addEventListener("xynth:new-home-chat", handleNewHomeChat);

    return () => {
      window.removeEventListener("xynth:new-home-chat", handleNewHomeChat);
    };
  }, [bumpResetKey]);

  return <PageContent key={resetKey} />;
}

export function HomeClient() {
  return (
    <Suspense fallback={null}>
      <PageContentWithReset />
    </Suspense>
  );
}
